import { ChevronUp, ChevronDown, Trash2, EyeOff, Eye, Package } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { parseNum, formatINR } from "@/lib/qgCalc";
import { ImageField } from "./ImageField";
import { AutoTextarea } from "./AutoTextarea";

const NUMERIC = new Set(["quantity", "pieces", "area_sqft", "rate", "lump_sum"]);
const WIDE = new Set(["product_name", "specification", "size_capacity", "remarks", "dimensions"]);
const BASIS = [
  ["qty_rate", "Quantity × per-piece rate"],
  ["area_total", "Total area × sq.ft rate"],
  ["qty_area", "Quantity × area per piece × sq.ft rate"],
  ["lump_sum", "Lump-sum amount"],
];

const ring = (bad) => (bad ? "border-red-400 ring-1 ring-red-200 focus-visible:ring-red-300" : "");

function Field({ label, value, onChange, bad, readOnly, numeric, testId, wide, money }) {
  const normalize = () => {
    if (!money) return;
    const p = parseNum(value);
    if (p.val === null || p.val === undefined || p.error) return;
    const fixed = formatINR((p.val * 2n + 100n) / 200n).replace(/,/g, "");
    if (fixed !== String(value).trim()) onChange(fixed);
  };
  return (
    <label className={`block space-y-1 ${wide ? "col-span-2" : ""}`}>
      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} onBlur={normalize} disabled={readOnly} inputMode={numeric ? "decimal" : undefined}
        className={`h-9 rounded-md bg-white dark:bg-slate-900 text-sm ${numeric ? "tabular-nums text-right" : ""} ${ring(bad)}`} data-testid={testId} />
    </label>
  );
}

function AmountBox({ state, amount, complete, item }) {
  let body;
  if (state === "excluded") body = <span className="text-slate-400">Excluded</span>;
  else if (state === "covered") body = <span className="text-slate-500 text-xs">Not charged — covered by pricing choice</span>;
  else if (amount === null) body = <span className="text-red-500 text-xs font-medium">Incomplete</span>;
  else if (amount === 0n && item.zero_rate_confirmed) body = <span className="text-emerald-700 text-xs font-medium">Included · no extra charge</span>;
  else body = <span className="tabular-nums">{formatINR(amount, true)}</span>;
  return (
    <div className={`rounded-lg px-3 py-2 text-right ${complete ? "bg-slate-100 dark:bg-slate-900" : "bg-red-50 dark:bg-red-950/30"}`}>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Amount · calculated</p>
      <p className="text-base font-bold text-slate-900 dark:text-white" data-testid={`qg-item-amount-${item.id}`}>{body}</p>
    </div>
  );
}

export function ItemCard({ section, group, item, calcItem, errors, readOnly, actions, index, count }) {
  const set = (patch) => actions.setItem(section.key, group.id, item.id, patch);
  const fields = section.columns.map((c) => c.field);
  const labels = Object.fromEntries(section.columns.map((c) => [c.field, c.label]));
  const bad = new Set(calcItem?.missing || []);
  const gridFields = fields.filter((f) => !["sr_no", "description", "amount", "image"].includes(f));
  const furniture = section.layout === "furniture";
  const basis = item.pricing_basis || "qty_rate";
  const rate = parseNum(item.rate);
  const zeroRate = rate.val !== null && rate.val !== undefined && rate.val < 50n;
  const state = calcItem?.state || "billable";
  const testBase = `qg-item-${item.id}`;

  return (
    <div id={testBase} data-testid={testBase}
      className={`rounded-xl border p-4 transition-colors ${state === "excluded" ? "opacity-60 border-dashed border-slate-300 bg-slate-50 dark:bg-slate-900/40" : state === "covered" ? "border-dashed border-slate-300 bg-slate-50/60 dark:bg-slate-900/30" : calcItem?.complete ? "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800" : "border-red-200 dark:border-red-900 bg-white dark:bg-slate-800"}`}>
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex lg:flex-col items-center gap-1 lg:w-14 shrink-0">
          <Input value={item.sr_no || ""} onChange={(e) => set({ sr_no: e.target.value })} disabled={readOnly} placeholder={String(index + 1)}
            className="h-8 w-16 lg:w-14 px-1 text-center text-xs font-mono rounded-md" data-testid={`${testBase}-sr`} title="Serial number" />
          {!readOnly && (
            <>
              <button type="button" disabled={index === 0} onClick={() => actions.moveItem(section.key, group.id, item.id, -1)} className="p-1 rounded text-slate-400 hover:text-slate-900 disabled:opacity-30" data-testid={`${testBase}-up`} title="Move up"><ChevronUp className="h-4 w-4" /></button>
              <button type="button" disabled={index === count - 1} onClick={() => actions.moveItem(section.key, group.id, item.id, 1)} className="p-1 rounded text-slate-400 hover:text-slate-900 disabled:opacity-30" data-testid={`${testBase}-down`} title="Move down"><ChevronDown className="h-4 w-4" /></button>
            </>
          )}
        </div>

        <div className="flex-1 min-w-0 space-y-3">
          {item.package_role && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">
              <Package className="h-3 w-3" />{item.package_role === "package" ? "Package / turnkey price" : "Itemized component"}
            </span>
          )}
          {fields.includes("description") && (
            <label className="block space-y-1">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{labels.description}</span>
              <AutoTextarea value={item.description} onChange={(v) => set({ description: v })} readOnly={readOnly} bad={bad.has("description")} testId={`${testBase}-description`} />
            </label>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
            {gridFields.map((f) => (
              <Field key={f} label={f === "rate" && furniture ? (basis === "qty_rate" ? "Rate per piece" : "Rate per sq.ft") : labels[f]} value={item[f]}
                onChange={(v) => set({ [f]: v })} bad={bad.has(f) || (f === "product_name" && bad.has("description"))} readOnly={readOnly || (f === "rate" && basis === "lump_sum")}
                numeric={NUMERIC.has(f)} money={f === "rate"} wide={WIDE.has(f)} testId={`${testBase}-${f}`} />
            ))}
          </div>
          {furniture && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end rounded-lg bg-amber-50/60 dark:bg-amber-950/20 border border-amber-100 dark:border-amber-900 p-3">
              <label className="block space-y-1 md:col-span-2">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-800">Pricing basis</span>
                <Select value={basis} onValueChange={(v) => set({ pricing_basis: v })} disabled={readOnly}>
                  <SelectTrigger className="h-9 bg-white dark:bg-slate-900" data-testid={`${testBase}-basis`}><SelectValue /></SelectTrigger>
                  <SelectContent>{BASIS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </label>
              {basis === "lump_sum" && <Field label="Lump-sum amount" value={item.lump_sum} onChange={(v) => set({ lump_sum: v })} bad={bad.has("lump_sum")} readOnly={readOnly} numeric money testId={`${testBase}-lump_sum`} />}
              {basis === "qty_area" && <p className="text-[11px] text-amber-800">Area is per piece; amount = qty × area × rate.</p>}
              {basis === "area_total" && <p className="text-[11px] text-amber-800">Area is the total billable area; quantity is not multiplied again.</p>}
              {item.area_needs_review && basis !== "qty_rate" && basis !== "lump_sum" && (
                <label className="md:col-span-3 flex items-center gap-2 text-xs text-amber-900">
                  <Checkbox checked={!!item.area_confirmed} onCheckedChange={(v) => set({ area_confirmed: !!v })} disabled={readOnly} data-testid={`${testBase}-area-confirm`} />
                  Template area ({item.area_sqft || "—"} sq.ft) reviewed and confirmed for this project
                </label>
              )}
            </div>
          )}
          {zeroRate && (
            <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
              <Checkbox checked={!!item.zero_rate_confirmed} onCheckedChange={(v) => set({ zero_rate_confirmed: !!v })} disabled={readOnly} data-testid={`${testBase}-zero-confirm`} />
              Included at no additional charge (rate 0)
            </label>
          )}
          {state === "billable" && errors.length > 0 && (
            <ul className="text-xs text-red-600 space-y-0.5" data-testid={`${testBase}-errors`}>{errors.map((e) => <li key={e.field + e.message}>• {e.message}</li>)}</ul>
          )}
        </div>

        <div className="lg:w-48 shrink-0 space-y-2">
          <AmountBox state={state} amount={calcItem?.amount ?? null} complete={calcItem?.complete} item={item} />
          {fields.includes("image") && <ImageField imageId={item.image_id} onChange={(v) => set({ image_id: v })} readOnly={readOnly} testId={`${testBase}-image`} />}
          {!readOnly && (
            <div className="flex gap-1.5">
              <button type="button" onClick={() => set({ excluded: !item.excluded })} data-testid={`${testBase}-exclude-toggle`}
                className="flex-1 inline-flex items-center justify-center gap-1 text-xs font-medium rounded-md border border-slate-200 dark:border-slate-600 py-1.5 hover:border-slate-400 transition-colors">
                {item.excluded ? <><Eye className="h-3.5 w-3.5" />Include</> : <><EyeOff className="h-3.5 w-3.5" />Exclude</>}
              </button>
              {item.custom && (
                <button type="button" onClick={() => actions.removeItem(section.key, group.id, item.id)} data-testid={`${testBase}-delete`} title="Delete custom item"
                  className="px-2.5 rounded-md border border-slate-200 dark:border-slate-600 text-slate-500 hover:text-red-600 hover:border-red-300 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
